import { createSignal, For, Match, Show, Switch } from "solid-js";
import { Title } from "@solidjs/meta";
import { router } from "~/inertia";
import { Button } from "~/common/button";
import { Pill } from "~/common/pill";
import { PublicFormShell } from "~/common/public_form/shell";
import { PublicFormHeader } from "~/common/public_form/header";
import { SuccessCheck } from "~/common/public_form/success_check";
import { formatPhoneNumber } from "~/util/format";
import { familyVisitHubPath, visitHubAddOnPath, visitHubFormsPath, visitHubPath } from "~/routes";
import { AddOnCard } from "~/visit_hub/add_on_card";
import {
  createPatientText,
  formatVisitTime,
  type PatientI18n,
  PatientTextProvider,
  splitAround,
  usePatientText,
} from "~/patient_i18n/i18n";
import { PatientLanguageBar } from "~/patient_i18n/language_bar";

type Header = {
  locationName: string;
  logoUrl: string | null;
  addressLines: string[] | null;
  phone: string | null;
};

type AppointmentRow = {
  // ISO time on the office's clock; the page formats it in the patient's language.
  startsAt: string;
  timeZone: string;
  providerName: string | null;
  confirmed: boolean;
  // Only on the "also coming up" rows: the day being acted on is the header's office.
  locationName?: string;
};

type AddOn = {
  id: string;
  name: string;
  description: string | null;
  priceCents: number | null;
  discountCents: number | null;
  added: boolean;
};

type Props = {
  state: "confirmable" | "forms_pending" | "all_set" | "no_upcoming";
  token: string;
  header: Header;
  patientFirstName: string;
  // Present only for the day-having states (confirmable / forms_pending / all_set).
  appointments?: AppointmentRow[];
  // Every other upcoming visit, at any of the practice's offices.
  upcoming?: AppointmentRow[];
  addOns?: AddOn[];
  discountPending?: boolean;
  // Echoed back by the confirm/add-on POSTs so the server no-ops when the
  // rendered day went stale under an open tab.
  anchorDate?: string;
  // Also sent for no_upcoming (new-patient forms render there too).
  formsNeeded?: number;
  // Present only on the responsible party's link: the quiet door to the
  // family's day on the /fv page.
  familyHubToken?: string | null;
  patientI18n: PatientI18n;
};

export default function VisitHubShow(props: Props) {
  // Optional per-state props, defaulted once for every branch.
  const appointments = () => props.appointments ?? [];
  const upcoming = () => props.upcoming ?? [];
  const addOns = () => props.addOns ?? [];
  const discountPending = () => props.discountPending ?? false;
  const formsNeeded = () => props.formsNeeded ?? 0;
  const anchorDate = () => props.anchorDate ?? "";
  const familyHubToken = () => props.familyHubToken ?? null;
  // The language only changes on a full page load, so the strings are read once.
  // eslint-disable-next-line solid/reactivity
  const text = createPatientText(props.patientI18n);
  const t = text.t;

  return (
    <PatientTextProvider value={text}>
      <Title>{t("visit_hub.page_title", { location: props.header.locationName })}</Title>

      <PublicFormShell
        width="md"
        poweredByLabel={t("common.powered_by")}
        toolbar={<PatientLanguageBar />}
      >
        <PublicFormHeader
          location={{
            name: props.header.locationName,
            address: props.header.addressLines?.join(", ") ?? null,
            phone: props.header.phone,
            logoUrl: props.header.logoUrl,
          }}
          class="border-b border-brand-subtle bg-brand-light px-6 py-5"
        />

        <div class="flex flex-col gap-8 px-6 py-8">
          <Switch>
            <Match when={props.state === "confirmable"}>
              <ConfirmableBody
                patientFirstName={props.patientFirstName}
                appointments={appointments()}
                upcoming={upcoming()}
                addOns={addOns()}
                discountPending={discountPending()}
                formsNeeded={formsNeeded()}
                token={props.token}
                anchorDate={anchorDate()}
                familyHubToken={familyHubToken()}
              />
            </Match>
            <Match when={props.state === "forms_pending" || props.state === "all_set"}>
              <ConfirmedBody
                appointments={appointments()}
                upcoming={upcoming()}
                addOns={addOns()}
                discountPending={discountPending()}
                formsNeeded={formsNeeded()}
                token={props.token}
                anchorDate={anchorDate()}
                familyHubToken={familyHubToken()}
              />
            </Match>
            <Match when={props.state === "no_upcoming"}>
              <NoUpcomingBody
                phone={props.header.phone}
                formsNeeded={formsNeeded()}
                token={props.token}
              />
            </Match>
          </Switch>
        </div>
      </PublicFormShell>
    </PatientTextProvider>
  );
}

function ConfirmableBody(props: {
  patientFirstName: string;
  appointments: AppointmentRow[];
  upcoming: AppointmentRow[];
  addOns: AddOn[];
  discountPending: boolean;
  formsNeeded: number;
  token: string;
  anchorDate: string;
  familyHubToken: string | null;
}) {
  const { t } = usePatientText();
  const [confirming, setConfirming] = createSignal(false);

  const confirm = () => {
    setConfirming(true);
    router.post(
      visitHubPath(props.token),
      { anchorDate: props.anchorDate },
      { onFinish: () => setConfirming(false) },
    );
  };

  return (
    <>
      <div>
        <h1 class="text-2xl font-semibold text-typography-primary">
          {t("visit_hub.greeting", { name: props.patientFirstName })}
        </h1>
        <p class="mt-2 text-sm text-typography-secondary">{t("visit_hub.tap_to_confirm")}</p>
      </div>

      <AppointmentList appointments={props.appointments} />

      <FamilyHubLink token={props.familyHubToken} />

      <div class="flex flex-col gap-2">
        <Button
          type="button"
          variant="primary"
          size="lg"
          width="full"
          loading={confirming()}
          disabled={confirming()}
          onClick={confirm}
        >
          {t("visit_hub.confirm")}
        </Button>
        <Show when={props.formsNeeded > 0}>
          <p class="text-center text-sm text-typography-secondary">{t("visit_hub.next_forms")}</p>
        </Show>
      </div>

      <AddOnsSection
        addOns={props.addOns}
        discountPending={props.discountPending}
        token={props.token}
        anchorDate={props.anchorDate}
      />

      <UpcomingSection appointments={props.upcoming} />
    </>
  );
}

function ConfirmedBody(props: {
  appointments: AppointmentRow[];
  upcoming: AppointmentRow[];
  addOns: AddOn[];
  discountPending: boolean;
  formsNeeded: number;
  token: string;
  anchorDate: string;
  familyHubToken: string | null;
}) {
  const { t } = usePatientText();

  return (
    <>
      <div class="flex flex-col items-center gap-4 text-center">
        <SuccessCheck />
        <div>
          <h1 class="text-2xl font-semibold text-typography-primary">
            {t("visit_hub.confirmed_title")}
          </h1>
          <p class="mt-1 text-sm text-typography-secondary">{t("visit_hub.see_you_soon")}</p>
        </div>
      </div>

      <AppointmentList appointments={props.appointments} />

      <FamilyHubLink token={props.familyHubToken} />

      <Show when={props.formsNeeded > 0}>
        <FormsCard
          formsNeeded={props.formsNeeded}
          token={props.token}
          hasVisit
          anchorDate={props.anchorDate}
        />
      </Show>

      <AddOnsSection
        addOns={props.addOns}
        discountPending={props.discountPending}
        token={props.token}
        anchorDate={props.anchorDate}
      />

      <UpcomingSection appointments={props.upcoming} />
    </>
  );
}

function NoUpcomingBody(props: { phone: string | null; formsNeeded: number; token: string }) {
  const { t } = usePatientText();

  return (
    <>
      <TerminalNotice title={t("visit_hub.no_upcoming")} phone={props.phone} />
      <Show when={props.formsNeeded > 0}>
        <FormsCard
          formsNeeded={props.formsNeeded}
          token={props.token}
          hasVisit={false}
          anchorDate=""
        />
      </Show>
    </>
  );
}

function FormsCard(props: {
  formsNeeded: number;
  token: string;
  hasVisit: boolean;
  anchorDate: string;
}) {
  const { t } = usePatientText();
  const [starting, setStarting] = createSignal(false);

  const start = () => {
    setStarting(true);
    // On success the server responds with an Inertia location redirect to the
    // Hotwire forms page — the client hard-navigates, so the button must STAY
    // loading until the new page loads (onFinish fires before the navigation
    // lands and would flicker the button back). Reset only on the paths where
    // the user stays on this page: error, cancel, or a network exception. The
    // 303-back path (state no longer eligible) refreshes props and unmounts
    // the card, so it cannot strand a spinner.
    const reset = () => setStarting(false);
    document.addEventListener("inertia:exception", reset, { once: true });
    document.addEventListener("inertia:invalid", reset, { once: true });
    // bfcache restore: navigating back from the forms page revives this page
    // with the signal still true — pageshow(persisted) is the only hook.
    window.addEventListener("pageshow", (e) => e.persisted && reset(), { once: true });
    // onSuccess covers the server's PRG-back path (ensure failed → 303 to this
    // page): props reconcile in place without a remount, so the card survives
    // and must un-busy. The happy path never fires onSuccess (409 hard visit).
    // The rendered anchor date rides along (blank when the page rendered
    // anchorless) — the server no-ops the assignment when the live anchor
    // has drifted from what this page showed, same as confirm and add-ons.
    router.post(
      visitHubFormsPath(props.token),
      { anchorDate: props.anchorDate },
      { onError: reset, onCancel: reset, onSuccess: reset },
    );
  };

  return (
    <div class="flex flex-col gap-3 rounded-xl border border-brand-subtle p-4">
      <div>
        <h2 class="text-base font-medium text-typography-primary">{t("visit_hub.forms_title")}</h2>
        <p class="mt-0.5 text-sm text-typography-secondary">
          {t(props.hasVisit ? "visit_hub.forms_to_fill_before_visit" : "visit_hub.forms_to_fill", {
            count: props.formsNeeded,
          })}{" "}
          {t("visit_hub.forms_quick")}
        </p>
      </div>

      <Button
        type="button"
        variant="secondary"
        width="full"
        leftIconName="document-text"
        loading={starting()}
        disabled={starting()}
        onClick={start}
      >
        {t("visit_hub.start_forms")}
      </Button>
    </div>
  );
}

function AppointmentList(props: { appointments: AppointmentRow[] }) {
  const { t, locale } = usePatientText();

  return (
    <ul class="flex flex-col gap-3">
      <For each={props.appointments}>
        {(appointment) => (
          <li class="flex items-center justify-between gap-3 rounded-xl border border-brand-subtle px-4 py-3">
            <div class="min-w-0">
              <p class="font-medium text-typography-primary">
                {formatVisitTime(appointment.startsAt, appointment.timeZone, locale)}
              </p>
              <Show when={appointment.providerName}>
                {(name) => (
                  <p class="mt-0.5 truncate text-sm text-typography-secondary">{name()}</p>
                )}
              </Show>
              <Show when={appointment.locationName}>
                {(name) => (
                  <p class="mt-0.5 truncate text-sm text-typography-secondary">{name()}</p>
                )}
              </Show>
            </div>
            <Show when={appointment.confirmed}>
              <Pill variant="success" size="sm" class="shrink-0">
                {t("visit_hub.confirmed")}
              </Pill>
            </Show>
          </li>
        )}
      </For>
    </ul>
  );
}

// The rest of what is booked, at any office. The day above is the only one this page acts on:
// once it is confirmed the page moves on to the next day that still needs confirming.
function UpcomingSection(props: { appointments: AppointmentRow[] }) {
  const { t } = usePatientText();

  return (
    <Show when={props.appointments.length > 0}>
      <div class="flex flex-col gap-3">
        <div>
          <h2 class="text-base font-semibold text-typography-primary">
            {t("visit_hub.also_coming_up")}
          </h2>
          <Show when={props.appointments.some((appointment) => !appointment.confirmed)}>
            <p class="mt-1 text-sm text-typography-secondary">
              {t("visit_hub.confirm_these_later")}
            </p>
          </Show>
        </div>
        <AppointmentList appointments={props.appointments} />
      </div>
    </Show>
  );
}

function AddOnsSection(props: {
  addOns: AddOn[];
  discountPending: boolean;
  token: string;
  anchorDate: string;
}) {
  const { t } = usePatientText();

  return (
    <Show when={props.addOns.length > 0}>
      <div class="flex flex-col gap-3">
        <div>
          <h2 class="text-base font-semibold text-typography-primary">
            {t("visit_hub.while_youre_in")}
          </h2>
          <Show when={props.discountPending}>
            <p class="mt-1 text-sm text-typography-secondary">
              {t("visit_hub.discount_at_office")}
            </p>
          </Show>
        </div>
        <For each={props.addOns}>
          {(addOn) => (
            <HubAddOnCard addOn={addOn} token={props.token} anchorDate={props.anchorDate} />
          )}
        </For>
      </div>
    </Show>
  );
}

function HubAddOnCard(props: { addOn: AddOn; token: string; anchorDate: string }) {
  const { t } = usePatientText();
  const [adding, setAdding] = createSignal(false);

  const add = () => {
    setAdding(true);
    router.post(
      visitHubAddOnPath(props.token, props.addOn.id),
      { anchorDate: props.anchorDate },
      { onFinish: () => setAdding(false) },
    );
  };

  return (
    <AddOnCard
      name={props.addOn.name}
      description={props.addOn.description}
      priceCents={props.addOn.priceCents}
      discountCents={props.addOn.discountCents}
      added={props.addOn.added}
      adding={adding()}
      onAdd={add}
      t={t}
    />
  );
}

// The responsible party's quiet door to the family's day. Interaction, not
// narration: two words, only where a family day exists.
function FamilyHubLink(props: { token: string | null }) {
  const { t } = usePatientText();

  return (
    <Show when={props.token}>
      {(token) => (
        <a href={familyVisitHubPath(token())} class="text-sm text-brand-link hover:underline">
          {t("visit_hub.family_link")}
        </a>
      )}
    </Show>
  );
}

function TerminalNotice(props: { title: string; phone: string | null }) {
  const { t } = usePatientText();
  const callUs = () => splitAround(t("common.call_us"), "phone");

  return (
    <div class="flex flex-col items-center gap-3 py-6 text-center">
      <h1 class="text-xl font-semibold text-typography-primary">{props.title}</h1>
      <Show when={props.phone}>
        {(phone) => (
          <p class="text-sm text-typography-secondary">
            {callUs()[0]}
            <a href={`tel:${phone()}`} class="font-medium text-brand-link hover:underline">
              {formatPhoneNumber(phone())}
            </a>
            {callUs()[1]}
          </p>
        )}
      </Show>
    </div>
  );
}
